/**
 * Utilities for exporting recording transcripts as .txt and .srt files.
 */
import type {
  RecordingMeta,
  RecordingTranscriptionSegment,
} from "./recordingsApi";

function pad(n: number, width = 2): string {
  return String(Math.max(0, Math.floor(n))).padStart(width, "0");
}

function formatClock(seconds: number): string {
  const s = Math.max(0, seconds);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = Math.floor(s % 60);
  if (hh > 0) return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
  return `${pad(mm)}:${pad(ss)}`;
}

function formatSrtTime(seconds: number): string {
  const s = Math.max(0, seconds);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = Math.floor(s % 60);
  const ms = Math.floor((s - Math.floor(s)) * 1000);
  return `${pad(hh)}:${pad(mm)}:${pad(ss)},${pad(ms, 3)}`;
}

function nameFor(seg: RecordingTranscriptionSegment): string {
  return seg.screenName || (seg.userId ? `User ${seg.userId}` : "Speaker");
}

export function transcriptToTxt(recording: RecordingMeta): string {
  const segs = recording.transcription?.segments || [];
  if (segs.length === 0) return "";
  const lines: string[] = [];
  const header = `Recording ${recording.id}`;
  const created = recording.createdTime ? ` — ${recording.createdTime}` : "";
  lines.push(`${header}${created}`);
  lines.push("");
  for (const seg of segs) {
    lines.push(`${nameFor(seg)} (${formatClock(seg.start)}): ${seg.text}`);
  }
  return lines.join("\n") + "\n";
}

export function transcriptToSrt(recording: RecordingMeta): string {
  const segs = recording.transcription?.segments || [];
  if (segs.length === 0) return "";
  const out: string[] = [];
  segs.forEach((seg, i) => {
    out.push(String(i + 1));
    out.push(`${formatSrtTime(seg.start)} --> ${formatSrtTime(seg.end)}`);
    out.push(`${nameFor(seg)}: ${seg.text}`);
    out.push("");
  });
  return out.join("\n");
}

export function downloadBlob(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { document.body.removeChild(a); } catch { /* */ }
    URL.revokeObjectURL(url);
  }, 0);
}

export function downloadTranscriptTxt(recording: RecordingMeta): void {
  const safeId = recording.id.replace(/[^a-z0-9_-]/gi, "");
  downloadBlob(`recording-${safeId}.txt`, transcriptToTxt(recording), "text/plain");
}

export function downloadTranscriptSrt(recording: RecordingMeta): void {
  const safeId = recording.id.replace(/[^a-z0-9_-]/gi, "");
  downloadBlob(`recording-${safeId}.srt`, transcriptToSrt(recording), "application/x-subrip");
}

export { formatClock as formatTranscriptClock };
